import { useQuery, QueryClient } from '@tanstack/react-query'
import { ApiError, apiGet } from '@/api/api.ts'
import { QueryCacheKeys } from '@/api/queryKeys.ts'
import useQueryGetMyPermission, { PermissionAction, PermissionSubject } from '@/api/auth/getPermission.ts'
import { SECOND } from '@/constant/time.ts'

export type TestSessionKind = 'emergency'

export interface ITestSession {
    id: number
    protecteeId: number
    kind: TestSessionKind
    deviceSerial: string
    createdByName: string
    createdAt: string
    endsAt: string
    endedAt: string | null
    endedByName: string | null
    isActive: boolean
    isMine: boolean
}

export interface IActiveTestSession extends ITestSession {
    protecteeName: string
}

const ACTIVE_TEST_SESSIONS_REFETCH_INTERVAL = 30 * SECOND

export const fetchActiveTestSessions = async (): Promise<IActiveTestSession[]> => {
    try {
        const { data } = await apiGet<IActiveTestSession[]>(`/protectee/test-session/active`)
        return Array.isArray(data) ? data : []
    } catch (error) {
        if (error instanceof ApiError && error.status === 404) return []
        throw error
    }
}

const useQueryGetActiveTestSessions = () => {
    const { hasPermission } = useQueryGetMyPermission()
    return useQuery({
        enabled: hasPermission(PermissionSubject.ALERT_EMERGENCY, PermissionAction.READ),
        queryKey: QueryCacheKeys.user.getActiveTestSessions(),
        refetchInterval: ACTIVE_TEST_SESSIONS_REFETCH_INTERVAL,
        queryFn: fetchActiveTestSessions
    })
}

export const invalidateTestSessionQueries = (queryClient: QueryClient) =>
    Promise.all([
        queryClient.invalidateQueries({ queryKey: QueryCacheKeys.user.getActiveTestSessions() }),
        queryClient.invalidateQueries({ queryKey: QueryCacheKeys.user.getUsersRoot() })
    ])

export default useQueryGetActiveTestSessions
